import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ProductAccessPanel } from '../components/ProductAccessPanel';
import { DiscountLimitsPanel } from '../components/DiscountLimitsPanel';
import { Buildings, Upload, Check, Wrench, X, Warning } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';

import { notify } from '../lib/notify';
import { InlineSkeleton, PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanySettings = () => {
  const { user } = useAuthStore();
  const isPresident = user?.role === 'President';
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const resp = await axios.get(`${API}/company-settings`);
      setSettings(resp.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        const logoResp = await axios.post(`${API}/company-settings/logo`, formData);
        settings.logo_url = logoResp.data.logo_url;
      }
      await axios.put(`${API}/company-settings`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      setLogoFile(null);
    } catch (error) {
      notify(error.response?.data?.detail || 'Error saving settings');
    } finally { setSaving(false); }
  };

  if (loading) return <PageSkeleton />;
  if (!settings) return <div className="p-4 sm:p-6 lg:p-8">Error loading settings</div>;

  const inputCls = "w-full px-4 py-2.5 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white text-sm";
  const labelCls = "block text-sm font-medium text-[#3B5A54] mb-1.5";

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="company-settings-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Company Settings</h1>
          <p className="text-[#6D8E87]">Manage your company details visible on all documents</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition ${saved ? 'bg-green-500 text-white' : 'bg-[#00a69c] text-white hover:bg-[#00bfb3]'} disabled:opacity-50`}
          data-testid="save-settings-btn">
          {saved ? <><Check size={20} weight="bold" /> Saved!</> : saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Logo */}
      <div className="medical-card p-6 mb-6" data-testid="logo-section">
        <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Company Logo</h3>
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 bg-[#F4F9F7] border-2 border-dashed border-[#D5E8E4] rounded-xl flex items-center justify-center overflow-hidden">
            {settings.logo_url ? (
              <img src={`${BACKEND_URL}${settings.logo_url}`} alt="Logo" className="w-full h-full object-contain p-2" />
            ) : (
              <Buildings size={40} className="text-[#D5E8E4]" />
            )}
          </div>
          <div>
            <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])}
              className="text-sm" data-testid="logo-upload" />
            <p className="text-xs text-[#6D8E87] mt-1">Recommended: 400x400px PNG/JPG. Used on quotations, WCC, CSC, and sidebar.</p>
            {logoFile && <p className="text-xs text-[#00a69c] font-medium mt-1">New logo selected — click "Save Changes" to upload</p>}
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="medical-card p-6 mb-6" data-testid="company-info-section">
        <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={labelCls}>Company Name *</label><input value={settings.company_name || ''} onChange={e => set('company_name', e.target.value)} className={inputCls} data-testid="company-name" /></div>
          <div><label className={labelCls}>GST Number</label><input value={settings.gst_number || ''} onChange={e => set('gst_number', e.target.value)} className={inputCls} placeholder="e.g., 27AAACM1234F1Z5" data-testid="company-gst" /></div>
          <div><label className={labelCls}>PAN Number</label><input value={settings.pan_number || ''} onChange={e => set('pan_number', e.target.value)} className={inputCls} placeholder="e.g., AAACM1234F" data-testid="company-pan" /></div>
          <div><label className={labelCls}>Phone / Mobile</label><input value={settings.phone || ''} onChange={e => set('phone', e.target.value)} className={inputCls} data-testid="company-phone" /></div>
          <div><label className={labelCls}>Email</label><input value={settings.email || ''} onChange={e => set('email', e.target.value)} className={inputCls} data-testid="company-email" /></div>
          <div><label className={labelCls}>Website</label><input value={settings.website || ''} onChange={e => set('website', e.target.value)} className={inputCls} placeholder="https://..." data-testid="company-website" /></div>
        </div>
      </div>

      {/* Address */}
      <div className="medical-card p-6 mb-6" data-testid="address-section">
        <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className={labelCls}>Street Address</label><input value={settings.address || ''} onChange={e => set('address', e.target.value)} className={inputCls} data-testid="company-address" /></div>
          <div><label className={labelCls}>City</label><input value={settings.city || ''} onChange={e => set('city', e.target.value)} className={inputCls} data-testid="company-city" /></div>
          <div><label className={labelCls}>State</label><input value={settings.state || ''} onChange={e => set('state', e.target.value)} className={inputCls} data-testid="company-state" /></div>
          <div><label className={labelCls}>Pincode</label><input value={settings.pincode || ''} onChange={e => set('pincode', e.target.value)} className={inputCls} data-testid="company-pincode" /></div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="medical-card p-6 mb-6" data-testid="bank-section">
        <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Bank Details (shown on Quotation PDFs)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className={labelCls}>Bank Name</label><input value={settings.bank_name || ''} onChange={e => set('bank_name', e.target.value)} className={inputCls} data-testid="bank-name" /></div>
          <div><label className={labelCls}>Account Number</label><input value={settings.bank_account_number || ''} onChange={e => set('bank_account_number', e.target.value)} className={inputCls} data-testid="bank-account" /></div>
          <div><label className={labelCls}>IFSC Code</label><input value={settings.bank_ifsc || ''} onChange={e => set('bank_ifsc', e.target.value)} className={inputCls} data-testid="bank-ifsc" /></div>
          <div><label className={labelCls}>Branch</label><input value={settings.bank_branch || ''} onChange={e => set('bank_branch', e.target.value)} className={inputCls} data-testid="bank-branch" /></div>
        </div>
      </div>

      {/* Discount policy */}
      <div className="medical-card p-6" data-testid="discount-policy-section">
        <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Discount Policy</h3>
        <p className="text-xs text-[#6D8E87] mb-4">
          Absolute company-wide maximum discount % on any quotation line. Even the President cannot exceed this on a single quote — larger reductions require editing the master catalog price (audit-logged). Set to <strong>0</strong> to disable this floor entirely.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Company-wide maximum discount (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={settings.discount_floor_pct ?? 0}
              onChange={(e) => set('discount_floor_pct', parseFloat(e.target.value) || 0)}
              className={inputCls}
              data-testid="discount-floor-pct"
              placeholder="e.g. 40 = no quote line can exceed 40% off MRP"
            />
          </div>
        </div>
        {isPresident && (
          <div className="mt-6 pt-5 border-t border-[#E2ECEA]" data-testid="discount-limits-section">
            <h4 className="text-base font-semibold text-[#052A23] mb-2" style={{ fontFamily: 'Outfit' }}>Discount limits by role</h4>
            <DiscountLimitsPanel />
          </div>
        )}
      </div>

      {/* iter132 — Stale-quote hint threshold (surfaced on Daily Activity visit cards).
          Zero = disable the "Draft new quote →" CTA entirely. */}
      <div className="medical-card p-6 mb-6" data-testid="stale-quote-section">
        <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Stale-Quote Hint</h3>
        <p className="text-xs text-[#6D8E87] mb-4">
          On the Daily Activity visit cards, a bright <strong>"✍ Draft new quote →"</strong> CTA appears when a customer's latest quote is older than this many days. Recommended: 30 days. Set to <strong>0</strong> to hide the CTA everywhere.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Stale-quote threshold (days)</label>
            <input
              type="number"
              min="0"
              max="365"
              step="1"
              value={settings.stale_quote_days ?? 30}
              onChange={(e) => set('stale_quote_days', parseInt(e.target.value, 10) || 0)}
              className={inputCls}
              data-testid="stale-quote-days"
              placeholder="e.g. 30"
            />
          </div>
        </div>
      </div>

      {/* Repair Quotation Totals — one-shot admin utility to fix any historical
          quotes that were saved with the double-discount bug (pre Jul-2026).
          Idempotent — safe to re-run. Rendered only for the President because
          the backend endpoint is 403-locked to that role anyway. */}
      {isPresident && <QuotationTotalsRepairCard />}

      {/* Vooxy Copilot — tenant kill switch (President only) */}
      {isPresident && <CopilotTogglePanel />}

      {isPresident && (
        <div className="medical-card p-6 mt-6" data-testid="product-access-section">
          <h3 className="text-lg font-semibold text-[#052A23] mb-2" style={{ fontFamily: 'Outfit' }}>Product Upload Access</h3>
          <ProductAccessPanel />
        </div>
      )}

      {/* Sales Coordinator Access (Feb 2026 — Phase 4 of SC RBAC).
          President only. Backend enforces the same guard on PUT. */}
      {isPresident && <SCAccessPanel />}
    </div>
  );
};

/**
 * Small self-contained panel that dry-runs the quotation totals repair,
 * shows the impact, then commits on user confirmation. Only visible to
 * Presidents (the endpoint enforces the same guard server-side).
 */
const QuotationTotalsRepairCard = () => {
  const [preview, setPreview] = useState(null);      // dry-run result
  const [running, setRunning] = useState(false);
  const [committed, setCommitted] = useState(null);  // successful live-run result
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runDry = async () => {
    setRunning(true); setError(''); setCommitted(null);
    try {
      const r = await axios.post(`${API}/admin/repair-quotation-totals?dry_run=true`);
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to preview repair');
    } finally { setRunning(false); }
  };

  const commit = async () => {
    setRunning(true); setError(''); setConfirmOpen(false);
    try {
      const r = await axios.post(`${API}/admin/repair-quotation-totals?dry_run=false`);
      setCommitted(r.data);
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to commit repair');
    } finally { setRunning(false); }
  };

  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-amber-200 p-6" data-testid="repair-quotation-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center shrink-0">
          <Wrench size={22} weight="duotone" className="text-amber-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Repair quotation totals</h2>
          <p className="text-sm text-[#6D8E87]">
            One-shot fix for quotes saved before the Jul&nbsp;2026 double-discount bug fix.
            Idempotent — safe to run any number of times. Always previews first.
          </p>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-700 border-l-4 border-red-500 pl-3 py-2 bg-red-50 rounded mb-3" data-testid="repair-error">
          {error}
        </div>
      )}

      {committed && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-3 text-sm" data-testid="repair-committed">
          <div className="font-semibold text-emerald-900 flex items-center gap-1 mb-1">
            <Check size={16} weight="bold" /> Repair complete
          </div>
          <div className="text-emerald-800">
            Examined <b>{committed.examined}</b> quote(s), repaired <b>{committed.repaired}</b>, {committed.skipped_already_correct} were already correct
            {committed.ambiguous_needs_human_review > 0 && `, ${committed.ambiguous_needs_human_review} flagged for manual review`}.
            Net restored to quote totals: <b>₹{Number(committed.total_delta_inr).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</b>.
          </div>
        </div>
      )}

      {preview && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3" data-testid="repair-preview">
          <div className="font-semibold text-amber-900 text-sm mb-2 flex items-center gap-1">
            <Warning size={16} weight="fill" /> Preview (dry-run, nothing written yet)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-xs">
            <Stat label="Examined" value={preview.examined} />
            <Stat label="Would repair" value={preview.would_repair} tone="warning" />
            <Stat label="Already correct" value={preview.skipped_already_correct} />
            <Stat label="Needs human review" value={preview.ambiguous_needs_human_review} tone={preview.ambiguous_needs_human_review > 0 ? 'warning' : 'default'} />
          </div>
          <div className="text-sm text-amber-900 mb-3">
            Net ₹ that would be restored to quote totals:{' '}
            <b>₹{Number(preview.total_delta_inr).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</b>
          </div>

          {preview.sample_changes?.length > 0 && (
            <details className="mb-3">
              <summary className="text-xs font-medium text-[#3B5A54] cursor-pointer hover:text-[#00a69c]" data-testid="repair-sample-toggle">
                Sample of {Math.min(preview.sample_changes.length, 50)} quote(s) — click to expand
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto text-xs bg-white rounded border border-[#D5E8E4]">
                <table className="w-full">
                  <thead className="bg-[#F4F9F7] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold text-[#3B5A54]">Lead</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-[#3B5A54]">Status</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-[#3B5A54]">Old total</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-[#3B5A54]">New total</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-emerald-700">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_changes.map((s, i) => (
                      <tr key={i} className="border-t border-[#D5E8E4]">
                        <td className="px-2 py-1 truncate max-w-[180px]" title={s.lead_name}>{s.lead_name || '—'}</td>
                        <td className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#6D8E87]">{s.status || '—'}</td>
                        <td className="px-2 py-1 text-right">₹{Number(s.old_total).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-1 text-right font-semibold">₹{Number(s.new_total).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-1 text-right font-bold text-emerald-700">+₹{Number(s.delta).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setPreview(null)}
              className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
              data-testid="repair-discard-btn"
            >
              Discard preview
            </button>
            {preview.would_repair > 0 ? (
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={running}
                className="px-5 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                data-testid="repair-commit-btn"
              >
                Commit repair to database
              </button>
            ) : (
              <span className="text-xs text-emerald-700 self-center font-medium">Nothing to repair — DB is already clean ✅</span>
            )}
          </div>
        </div>
      )}

      {!preview && !committed && (
        <button
          onClick={runDry}
          disabled={running}
          className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="repair-preview-btn"
        >
          <Wrench size={16} weight="bold" /> {running ? 'Previewing…' : 'Preview repair (dry-run)'}
        </button>
      )}

      {committed && (
        <button
          onClick={() => { setCommitted(null); }}
          className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
          data-testid="repair-close-btn"
        >
          Close
        </button>
      )}

      {confirmOpen && preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="repair-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center shrink-0">
                <Warning size={22} weight="duotone" className="text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Confirm database write</h3>
                <p className="text-sm text-[#6D8E87] mt-1">
                  This will rewrite <b>{preview.would_repair}</b> quote(s) with corrected totals.
                  Approved &amp; already-shared quotes will also be updated. This action cannot be undone.
                </p>
              </div>
              <button onClick={() => setConfirmOpen(false)} className="text-[#6D8E87] hover:text-[#052A23]">
                <X size={20} />
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
                data-testid="repair-cancel-btn">Cancel</button>
              <button onClick={commit} disabled={running}
                className="px-5 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                data-testid="repair-confirm-btn">
                {running ? 'Committing…' : `Yes, repair ${preview.would_repair} quote(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = 'default' }) => (
  <div className={`rounded-lg px-3 py-2 border ${tone === 'warning' ? 'border-amber-300 bg-amber-100/50' : 'border-[#D5E8E4] bg-white'}`}>
    <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">{label}</div>
    <div className={`text-lg font-bold ${tone === 'warning' ? 'text-amber-800' : 'text-[#052A23]'}`} style={{ fontFamily: 'Outfit' }}>{value}</div>
  </div>
);


/**
 * SCAccessPanel — President-only toggles controlling what a Sales Coordinator
 * on this tenant can do. Reads/writes `/api/sc-permissions`. Each flag is a
 * boolean; changes are debounced-saved on toggle so the panel never shows a
 * separate "Save" button. The flag definitions and defaults live on the
 * backend (`sc_permissions.py::DEFAULT_SC_PERMISSIONS`).
 */
const SCAccessPanel = () => {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState('');  // id of flag mid-save
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/sc-permissions`)
      .then((r) => setFlags(r.data?.flags || null))
      .catch(() => setError('Could not load SC access flags'));
  }, []);

  const toggle = async (key) => {
    if (!flags) return;
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    setSaving(key);
    setError('');
    try {
      const r = await axios.put(`${API}/sc-permissions`, { flags: { [key]: next[key] } });
      setFlags(r.data?.flags || next);
    } catch (e) {
      // Roll back on error so the UI reflects the server's truth.
      setFlags(flags);
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving('');
    }
  };

  const ROWS = [
    { key: 'can_view_analytics_tab', label: 'Show Analytics tab',       sub: 'Gross Margin, AI Feedback, Email Log, Import History links in sidebar.' },
    { key: 'can_view_gm_numbers',    label: 'Reveal Gross Margin numbers', sub: 'When OFF, GM amounts show as "₹***" and percentages as "**%". Chart shapes still render.' },
    { key: 'can_export_csv',         label: 'Allow CSV export',         sub: 'Leads, customers, products, quotations CSV download.' },
    { key: 'can_bulk_reassign',      label: 'Allow bulk reassign',      sub: 'Select multiple leads and reassign in one action.' },
    { key: 'can_bulk_delete',        label: 'Allow bulk delete',        sub: 'Select multiple leads / customers / products / quotations and delete.' },
    { key: 'can_use_share_links',    label: 'Allow share links',        sub: 'Generate public share links for quotations and Proforma Invoices.' },
  ];

  return (
    <div className="medical-card p-6 mt-6" data-testid="sc-access-section">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Sales Coordinator Access</h3>
        {saving && <span className="text-xs text-[#6D8E87]">Saving…</span>}
      </div>
      <p className="text-sm text-[#6D8E87] mb-4">
        Sales Coordinators read every record on this tenant but can only edit records they created.
        Use these toggles to grant additional capabilities. Changes take effect on the next page load for the Sales Coordinator.
      </p>
      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="sc-access-error">
          {error}
        </div>
      )}
      {flags ? (
        <div className="space-y-2">
          {ROWS.map((r) => (
            <label
              key={r.key}
              className="flex items-start justify-between gap-4 px-4 py-3 rounded-lg border border-[#D5E8E4] hover:border-[#00a69c]/30 transition cursor-pointer"
              data-testid={`sc-flag-${r.key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#052A23]">{r.label}</div>
                <div className="text-xs text-[#6D8E87] mt-0.5 leading-snug">{r.sub}</div>
              </div>
              <input
                type="checkbox"
                checked={!!flags[r.key]}
                onChange={() => toggle(r.key)}
                disabled={saving === r.key}
                className="mt-1 h-5 w-5 accent-[#00a69c] cursor-pointer disabled:opacity-50"
                data-testid={`sc-flag-${r.key}-input`}
              />
            </label>
          ))}
        </div>
      ) : (
        <InlineSkeleton />
      )}
    </div>
  );
};

/**
 * CopilotTogglePanel — President-only on/off switch for the Vooxy Copilot
 * AI assistant on this tenant. Flipping OFF hides the floating widget
 * for every user on the workspace instantly and returns 403 on backend
 * chat calls. Super Admin can also flip this flag from their console.
 */
const CopilotTogglePanel = () => {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/copilot/settings`)
      .then((r) => setEnabled(r.data?.enabled !== false))
      .catch(() => setError('Could not load Copilot setting'));
  }, []);

  const toggle = async () => {
    if (enabled === null) return;
    const next = !enabled;
    setSaving(true);
    setError('');
    try {
      const r = await axios.put(`${API}/copilot/settings`, { enabled: next });
      setEnabled(r.data?.enabled !== false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="medical-card p-6 mt-6" data-testid="copilot-toggle-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Vooxy Copilot (AI Assistant)</h3>
          <p className="text-sm text-[#6D8E87] mt-1 leading-snug">
            Floating chat assistant that answers natural-language questions about your CRM data,
            strictly scoped to each user's role. When OFF, the widget is hidden for every user on this workspace.
          </p>
          <div className="text-[11px] text-emerald-700 mt-2 font-medium">Currently in launch beta — free until the end of the beta period.</div>
          {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
        </div>
        <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1" data-testid="copilot-toggle-label">
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={toggle}
            disabled={saving || enabled === null}
            className="sr-only peer"
            data-testid="copilot-toggle-input"
          />
          <div className="w-12 h-6 bg-slate-300 rounded-full peer peer-focus:outline-none peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-6 peer-disabled:opacity-50"></div>
        </label>
      </div>
      <div className="text-[11px] text-[#6D8E87] mt-3">
        Status: <span className={`font-semibold ${enabled ? 'text-emerald-700' : 'text-red-600'}`}>
          {enabled === null ? '…' : enabled ? 'Enabled for all users' : 'Disabled — widget hidden'}
        </span>
        {saving && <span className="ml-2 italic">Saving…</span>}
      </div>
    </div>
  );
};


export default CompanySettings;
